/**
 * Ingame-Admin-Berechtigungen (Issue #7, Slice 1). EIGENER Namespace, bewusst
 * GETRENNT vom Web-Permission-System (`auth/permissions.ts`): das sind Rechte,
 * die ein PID-basierter Ingame-Admin später INGAME bekommt — keine Web-Actions.
 *
 * Die Web-Permission zum VERWALTEN dieser Einträge ist `action.manage_ingame_admins`
 * und liegt im normalen Web-`PERMISSIONS`-Array.
 *
 * Reine, DOM-/DB-freie Logik (testbar): Konstanten, Rollen-Presets, Preset-
 * Erkennung und Permission-Validierung.
 */

export const INGAME_PERMISSIONS = [
  "action.ingame.warn",
  "action.ingame.say",
  "action.ingame.kick",
  "action.ingame.temp_ban",
  "action.ingame.pid_ban",
  "action.ingame.autobalance",
  // Starke Server-Control-Kommandos (Issue #11). Bewusst NUR in admin/custom.
  "action.ingame.restart",
  "action.ingame.next_map",
  "action.ingame.tickets",
  "action.ingame.fbf",
  "action.ingame.double_tickets",
] as const;

export type IngamePermission = (typeof INGAME_PERMISSIONS)[number];

const INGAME_PERMISSION_SET = new Set<string>(INGAME_PERMISSIONS);

export type IngameRole = "helper" | "moderator" | "admin" | "custom";

export const INGAME_ROLES: IngameRole[] = ["helper", "moderator", "admin", "custom"];

/** Rollen-Presets (Issue #7). `custom` hat KEIN festes Set (freie Auswahl). */
export const INGAME_ROLE_PRESETS: Record<Exclude<IngameRole, "custom">, IngamePermission[]> = {
  // helper/moderator bekommen KEINE starken Server-Control-Rechte (Issue #11).
  // moderator behält bewusst nur temp_ban als „kleine" Ban-Variante.
  helper: ["action.ingame.warn", "action.ingame.say"],
  moderator: ["action.ingame.warn", "action.ingame.say", "action.ingame.kick", "action.ingame.temp_ban"],
  admin: [
    "action.ingame.warn",
    "action.ingame.say",
    "action.ingame.kick",
    "action.ingame.temp_ban",
    "action.ingame.pid_ban",
    "action.ingame.autobalance",
    "action.ingame.restart",
    "action.ingame.next_map",
    "action.ingame.tickets",
    "action.ingame.fbf",
    "action.ingame.double_tickets",
  ],
};

/** Ist `value` eine gültige Ingame-Permission? */
export function isIngamePermission(value: unknown): value is IngamePermission {
  return typeof value === "string" && INGAME_PERMISSION_SET.has(value);
}

/** Ist `value` eine gültige Ingame-Rolle? */
export function isIngameRole(value: unknown): value is IngameRole {
  return value === "helper" || value === "moderator" || value === "admin" || value === "custom";
}

/** Filtert/normalisiert eine beliebige Eingabe auf gültige, eindeutige Ingame-Permissions. */
export function normalizeIngamePermissions(input: unknown): IngamePermission[] {
  const arr = Array.isArray(input) ? input : [];
  const out = new Set<IngamePermission>();
  for (const p of arr) if (isIngamePermission(p)) out.add(p);
  // stabile Reihenfolge wie INGAME_PERMISSIONS
  return INGAME_PERMISSIONS.filter((p) => out.has(p));
}

/** Permissions eines Presets (custom -> leer; Aufrufer liefert eigene Liste). */
export function presetPermissions(role: IngameRole): IngamePermission[] {
  if (role === "custom") return [];
  return [...INGAME_ROLE_PRESETS[role]];
}

/**
 * Leitet aus einer Permission-Auswahl die passende Rolle ab: exakt ein Preset
 * -> dessen Name, sonst `custom`. So wird im Editor bei manueller Änderung
 * automatisch auf `custom` umgeschaltet (bzw. zurück auf ein Preset, wenn die
 * Auswahl wieder exakt passt).
 */
export function roleForPermissions(permissions: readonly string[]): IngameRole {
  const norm = normalizeIngamePermissions(permissions);
  const key = norm.join(",");
  for (const role of ["helper", "moderator", "admin"] as const) {
    if (presetPermissions(role).join(",") === key) return role;
  }
  return "custom";
}
